import { useEffect, useState } from 'react';
import api from '../services/api';
import { Link } from 'react-router-dom';

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  total_cents: number;
  status: string;
  delivery_date: string;
  delivery_option: string;
  created_at: string;
}

interface OrdersSummary {
  total: number;
  pending: number;
  confirmed: number;
  preparing: number;
  ready: number;
  completed: number;
  cancelled: number;
  total_revenue_cents: number;
  recent_orders: Order[];
}

export default function OrdersSummary() {
  const [ordersSummary, setOrdersSummary] = useState<OrdersSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [allOrders, setAllOrders] = useState<any[]>([]);

  useEffect(() => {
    loadOrdersData();
  }, []);

  useEffect(() => {
    if (allOrders.length > 0) {
      calculateSummary(allOrders);
    }
  }, [startDate, endDate, allOrders]);

  const loadOrdersData = async () => {
    try {
      const ordersResponse = await api.get('/orders/admin');

      // L'API peut retourner soit un tableau, soit un objet avec une propriété 'data' ou 'orders'
      const orders = Array.isArray(ordersResponse)
        ? ordersResponse
        : (ordersResponse?.data || ordersResponse?.orders || []);

      setAllOrders(orders);
      calculateSummary(orders);
    } catch (error) {
      console.error('Failed to load orders data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (orders: any[]) => {
    // Filtrer par date si des dates sont sélectionnées
    let filteredOrders = orders;

    if (startDate) {
      filteredOrders = filteredOrders.filter((o: any) => {
        const orderDate = new Date(o.created_at).toISOString().split('T')[0];
        return orderDate >= startDate;
      });
    }

    if (endDate) {
      filteredOrders = filteredOrders.filter((o: any) => {
        const orderDate = new Date(o.created_at).toISOString().split('T')[0];
        return orderDate <= endDate;
      });
    }

    // Calculer les statistiques des commandes
    const summary: OrdersSummary = {
      total: filteredOrders.length,
      pending: filteredOrders.filter((o: any) => o.status === 'pending').length,
      confirmed: filteredOrders.filter((o: any) => o.status === 'confirmed').length,
      preparing: filteredOrders.filter((o: any) => o.status === 'preparing').length,
      ready: filteredOrders.filter((o: any) => o.status === 'ready').length,
      completed: filteredOrders.filter((o: any) => o.status === 'completed').length,
      cancelled: filteredOrders.filter((o: any) => o.status === 'cancelled').length,
      total_revenue_cents: filteredOrders
        .filter((o: any) => o.status !== 'cancelled')
        .reduce((sum: number, o: any) => sum + (Number(o.total_cents) || 0), 0),
      recent_orders: filteredOrders
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10)
        .map((o: any) => ({
          id: o.id,
          order_number: o.order_number,
          customer_name: `${o.customer?.first_name || ''} ${o.customer?.last_name || ''}`.trim() || 'Client',
          total_cents: Number(o.total_cents) || 0,
          status: o.status,
          delivery_date: o.delivery_date,
          delivery_option: o.delivery_option || 'delivery',
          created_at: o.created_at,
        })),
    };

    setOrdersSummary(summary);
  };

  const resetFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des commandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Synthèse Commandes</h1>
          <p className="mt-1 text-sm text-gray-500">Vue d'ensemble de l'activité e-commerce</p>
        </div>
      </div>

      {/* Filtres de date */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">📅 Filtrer par période</h2>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de fin
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <button
            onClick={resetFilters}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Réinitialiser
          </button>
        </div>
        {(startDate || endDate) && (
          <div className="mt-3 text-sm text-gray-600">
            {startDate && endDate ? (
              <span>📊 Période: du {new Date(startDate).toLocaleDateString('fr-FR')} au {new Date(endDate).toLocaleDateString('fr-FR')}</span>
            ) : startDate ? (
              <span>📊 À partir du {new Date(startDate).toLocaleDateString('fr-FR')}</span>
            ) : (
              <span>📊 Jusqu'au {new Date(endDate).toLocaleDateString('fr-FR')}</span>
            )}
          </div>
        )}
      </div>

      {/* Synthèse des Commandes */}
      {ordersSummary && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">📊 Statistiques E-Commerce</h2>
          </div>

          {/* Statistiques par statut */}
          <div className="p-4 md:p-6 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{ordersSummary.total}</p>
              <p className="text-sm text-gray-600 mt-1">Total</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-600">{ordersSummary.pending}</p>
              <p className="text-sm text-gray-600 mt-1">En attente</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{ordersSummary.confirmed}</p>
              <p className="text-sm text-gray-600 mt-1">Confirmées</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">{ordersSummary.preparing}</p>
              <p className="text-sm text-gray-600 mt-1">Préparation</p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-3xl font-bold text-orange-600">{ordersSummary.ready}</p>
              <p className="text-sm text-gray-600 mt-1">Prêtes</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{ordersSummary.completed}</p>
              <p className="text-sm text-gray-600 mt-1">Terminées</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-bold text-red-600">{ordersSummary.cancelled}</p>
              <p className="text-sm text-gray-600 mt-1">Annulées</p>
            </div>
          </div>

          {/* Chiffre d'affaires */}
          <div className="px-6 pb-6">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-6 border-2 border-emerald-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-700">Chiffre d'affaires total</p>
                  <p className="text-4xl font-bold text-emerald-900 mt-2">
                    {(ordersSummary.total_revenue_cents / 100).toFixed(2)} €
                  </p>
                </div>
                <div className="text-6xl">💰</div>
              </div>
            </div>
          </div>

          {/* Commandes récentes */}
          <div className="border-t border-gray-200">
            <div className="p-4 md:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Commandes Récentes</h3>

              {/* Mobile: Card View */}
              <div className="md:hidden space-y-2">
                {ordersSummary.recent_orders.map((order) => {
                  const statusConfig: Record<string, { label: string; color: string }> = {
                    pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
                    confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-800' },
                    preparing: { label: 'Préparation', color: 'bg-purple-100 text-purple-800' },
                    ready: { label: 'Prête', color: 'bg-orange-100 text-orange-800' },
                    completed: { label: 'Terminée', color: 'bg-green-100 text-green-800' },
                    cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800' },
                  };
                  const sc = statusConfig[order.status] || { label: order.status, color: 'bg-gray-100 text-gray-800' };
                  return (
                    <Link key={order.id} to="/orders" className="block bg-gray-50 rounded-lg p-3 active:bg-gray-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-900">{order.order_number}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sc.color}`}>{sc.label}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm text-gray-600 truncate">{order.customer_name}</span>
                        <span className="text-sm font-bold text-gray-900">{(order.total_cents / 100).toFixed(2)} €</span>
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        {order.delivery_option === 'pickup' ? ' · Retrait' : ' · Livraison'}
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Desktop: Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Commande</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Livraison</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {ordersSummary.recent_orders.map((order) => {
                      const statusConfig: Record<string, { label: string; color: string }> = {
                        pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
                        confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-800' },
                        preparing: { label: 'Préparation', color: 'bg-purple-100 text-purple-800' },
                        ready: { label: 'Prête', color: 'bg-orange-100 text-orange-800' },
                        completed: { label: 'Terminée', color: 'bg-green-100 text-green-800' },
                        cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800' },
                      };
                      const sc = statusConfig[order.status] || { label: order.status, color: 'bg-gray-100 text-gray-800' };
                      const deliveryTypeConfig: Record<string, { label: string; icon: string; color: string }> = {
                        delivery: { label: 'Livraison', icon: '🚚', color: 'bg-blue-100 text-blue-800' },
                        pickup: { label: 'Retrait', icon: '🏪', color: 'bg-purple-100 text-purple-800' },
                      };
                      const dc = deliveryTypeConfig[order.delivery_option] || { label: 'Livraison', icon: '🚚', color: 'bg-blue-100 text-blue-800' };

                      return (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{order.order_number}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{order.customer_name}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${dc.color}`}>
                              <span>{dc.icon}</span><span>{dc.label}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">{(order.total_cents / 100).toFixed(2)} €</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sc.color}`}>{sc.label}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('fr-FR') : '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Link to="/orders" className="text-emerald-600 hover:text-emerald-900 font-medium">Voir →</Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {ordersSummary.recent_orders.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">Aucune commande pour le moment</p>
                </div>
              )}

              {ordersSummary.recent_orders.length > 0 && (
                <div className="mt-4 text-center">
                  <Link
                    to="/orders"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700"
                  >
                    Voir toutes les commandes
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
